/**
 * Salary structure component rules: Fixed vs Monthly Variable, active-as-of, Performance Bonus → Fixed CTC.
 */

export const METHOD_FIXED = "fixed";
export const METHOD_MONTHLY_VARIABLE = "monthlyVariable";

export const PB_FREQUENCIES = [
  { value: "annual", label: "Annual" },
  { value: "half_yearly", label: "Half-yearly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "monthly", label: "Monthly" },
];

/** Normalize legacy Method values into Fixed | Monthly Variable. */
export function normalizeCalculationMethod(component) {
  const method = String(component?.calculationMethod || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  const basis = String(component?.calculationBasis || component?.basis || "")
    .trim()
    .toLowerCase();

  if (
    method === "monthlyvariable" ||
    method === "onetime" ||
    basis === "recurrent" ||
    basis === "recurring"
  ) {
    return METHOD_MONTHLY_VARIABLE;
  }

  // Legacy Method=variable meant % of CTC/Basic — treat as Fixed package head
  return METHOD_FIXED;
}

export function isMonthlyVariableComponent(component) {
  return normalizeCalculationMethod(component) === METHOD_MONTHLY_VARIABLE;
}

/** Alias kept for call sites that used Basis=Recurrent. */
export function isRecurrentComponent(component) {
  return isMonthlyVariableComponent(component);
}

export function isFixedStructureComponent(component) {
  return normalizeCalculationMethod(component) === METHOD_FIXED;
}

/** Manual % under Fixed. Respect explicit manualAmountType first. */
export function isManualPercentComponent(component) {
  if (!component || isMonthlyVariableComponent(component)) return false;
  const amountType = String(component.manualAmountType || "")
    .trim()
    .toLowerCase();
  if (amountType === "amount") return false;
  if (amountType === "percent") return true;
  // Legacy Method=variable meant % of CTC/Basic
  const legacyVariable =
    String(component.calculationMethod || "")
      .trim()
      .toLowerCase() === "variable";
  if (legacyVariable) return true;
  const hasPercent =
    component.manualPercent !== "" &&
    component.manualPercent != null &&
    Number(component.manualPercent) > 0;
  const hasFixed = Number(component.manualAmount) > 0;
  return hasPercent && !hasFixed;
}

const HISTORY_FIELDS = [
  "calculationType",
  "calculationMethod",
  "calculationBasis",
  "manualAmount",
  "manualPercent",
  "manualAmountType",
  "percentBasis",
  "selectedFormula",
  "autoAdjustCTC",
  "isActive",
  "statusEffectiveFrom",
  "taxTreatment",
  "pfApplicable",
  "esiApplicable",
  "gratuityApplicable",
  "orderNo",
  "componentNature",
];

export function normalizeComponentNature(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (v === "nonpayroll" || v === "nonpay" || v === "notional") return "nonPayroll";
  return "payroll";
}

export function isNonPayrollComponent(component) {
  if (!component) return false;
  if (component.nonPayroll === true || component.isNonPayroll === true) return true;
  return normalizeComponentNature(component.componentNature) === "nonPayroll";
}

export function snapshotComponentConfig(component) {
  const snap = {};
  HISTORY_FIELDS.forEach((k) => {
    if (component && Object.prototype.hasOwnProperty.call(component, k)) {
      snap[k] = component[k];
    }
  });
  return snap;
}

/**
 * Append previous config to component.configHistory on Save (not on every field edit).
 * Caps at 25 entries.
 */
export function pushComponentConfigHistory(component, note = "") {
  if (!component) return component;
  const history = Array.isArray(component.configHistory)
    ? [...component.configHistory]
    : [];
  history.push({
    id: `h_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    changedAt: new Date().toISOString(),
    note: note || "Saved configuration",
    snapshot: snapshotComponentConfig(component),
  });
  while (history.length > 25) history.shift();
  return { ...component, configHistory: history };
}

/**
 * Restore a history entry onto the component (keeps history list; marks restore).
 */
export function restoreComponentFromHistory(component, historyId) {
  const history = Array.isArray(component?.configHistory)
    ? component.configHistory
    : [];
  const entry = history.find((h) => String(h.id) === String(historyId));
  if (!entry?.snapshot) return component;
  const withHist = pushComponentConfigHistory(
    component,
    `Restored from ${entry.changedAt || "history"}`
  );
  return {
    ...withHist,
    ...entry.snapshot,
    componentNature: normalizeComponentNature(
      entry.snapshot.componentNature || withHist.componentNature
    ),
    configHistory: withHist.configHistory,
  };
}

/**
 * Delete a history version and restore the previous remaining version into live config.
 */
export function deleteHistoryAndRestorePrevious(component, historyId) {
  const history = Array.isArray(component?.configHistory)
    ? [...component.configHistory]
    : [];
  const idx = history.findIndex((h) => String(h.id) === String(historyId));
  if (idx < 0) return { component, error: "History entry not found" };
  const removed = history[idx];
  history.splice(idx, 1);
  let restoreSnap = null;
  if (history.length > 0) restoreSnap = history[history.length - 1].snapshot;
  else if (removed?.snapshot) restoreSnap = removed.snapshot;
  let next = { ...component, configHistory: history };
  if (restoreSnap) {
    next = {
      ...next,
      ...restoreSnap,
      componentNature: normalizeComponentNature(
        restoreSnap.componentNature || next.componentNature
      ),
      configHistory: history,
    };
  }
  return { component: next, restoredFrom: restoreSnap ? history[history.length - 1] || removed : null };
}

/**
 * Component active as of a date (YYYY-MM or YYYY-MM-DD).
 * Missing statusEffectiveFrom → treat as always been in current isActive state.
 */
export function isComponentActiveAsOf(component, asOfDate) {
  if (!component) return false;
  const active = component.isActive !== false && component.active !== false;
  const from = String(component.statusEffectiveFrom || "").trim().slice(0, 10);
  if (!from) return active;
  const asOf = String(asOfDate || new Date().toISOString().slice(0, 10))
    .trim()
    .slice(0, 10);
  if (!asOf) return active;
  const fromKey = from.length === 7 ? `${from}-01` : from;
  const asOfKey = asOf.length === 7 ? `${asOf}-01` : asOf;
  if (asOfKey < fromKey) {
    // Before status change → opposite of current flag
    return !active;
  }
  return active;
}

export function filterComponentsForPackage(components = [], asOfDate) {
  return (components || []).filter(
    (c) =>
      isFixedStructureComponent(c) && isComponentActiveAsOf(c, asOfDate)
  );
}

export function filterComponentsForMonthlyVariables(components = [], asOfDate) {
  return (components || []).filter(
    (c) =>
      isMonthlyVariableComponent(c) && isComponentActiveAsOf(c, asOfDate)
  );
}

/**
 * Convert amount to annual using period monthly|annual.
 */
export function toAnnualAmount(amount, period = "annual") {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return 0;
  const p = String(period || "annual").toLowerCase();
  if (p === "monthly") return Math.round(n * 12 * 100) / 100;
  return Math.round(n * 100) / 100;
}

export function toMonthlyAmount(amount, period = "annual") {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return 0;
  const p = String(period || "annual").toLowerCase();
  if (p === "monthly") return Math.round(n * 100) / 100;
  return Math.round((n / 12) * 100) / 100;
}

/**
 * Fixed package CTC/Gross = Total − Performance Bonus (same period units after normalize to annual).
 * @returns {{ totalAnnual, pbAnnual, fixedAnnual, error?: string }}
 */
export function resolveFixedPackageCtc({
  totalAmount,
  totalPeriod = "annual",
  performanceBonusAmount = 0,
  performanceBonusPeriod = "annual",
} = {}) {
  const totalAnnual = toAnnualAmount(totalAmount, totalPeriod);
  const pbAnnual = toAnnualAmount(performanceBonusAmount, performanceBonusPeriod);
  if (pbAnnual > totalAnnual + 0.001) {
    return {
      totalAnnual,
      pbAnnual,
      fixedAnnual: 0,
      error: "Performance Bonus cannot exceed Total CTC / Gross",
    };
  }
  const fixedAnnual = Math.max(0, Math.round((totalAnnual - pbAnnual) * 100) / 100);
  return { totalAnnual, pbAnnual, fixedAnnual };
}

/**
 * Normalize a component for save/display after Method redesign.
 */
export function normalizeStructureComponent(component) {
  if (!component || typeof component !== "object") return component;
  const method = normalizeCalculationMethod(component);
  const next = { ...component };

  if (method === METHOD_MONTHLY_VARIABLE) {
    next.calculationMethod = METHOD_MONTHLY_VARIABLE;
    // Basis unused for monthly variable; keep last payDays for data shape
    if (
      String(next.calculationBasis || "").toLowerCase() === "recurrent" ||
      String(next.calculationBasis || "").toLowerCase() === "recurring"
    ) {
      next.calculationBasis = "payDays";
    }
  } else {
    next.calculationMethod = METHOD_FIXED;
    if (!next.manualAmountType) {
      next.manualAmountType = isManualPercentComponent({
        ...next,
        calculationMethod: METHOD_FIXED,
        manualAmountType: "",
      })
        ? "percent"
        : "amount";
    }
  }

  if (next.isActive === undefined && next.active !== undefined) {
    next.isActive = next.active !== false;
  }
  if (next.isActive === undefined) next.isActive = true;
  next.componentNature = normalizeComponentNature(next.componentNature || "payroll");

  return next;
}

export function normalizeStructureComponents(components = []) {
  return (components || []).map(normalizeStructureComponent);
}
